import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/layouts/DashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import AdvancedDashboardPage from "@/pages/AdvancedDashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import CategoriesPage from "@/pages/CategoriesPage";
import OrdersPage from "@/pages/OrdersPage";
import ProductsPage from "@/pages/ProductsPage";
import ShopifyProductsPage from "@/pages/ShopifyProductsPage";
import ShopifyOrdersPage from "@/pages/ShopifyOrdersPage";
import LogsPage from "@/pages/LogsPage";
import StoresPage from "@/pages/StoresPage";
import AutoSyncPage from "@/pages/AutoSyncPage";
import ServicesPage from "@/pages/ServicesPage";
import IncotermPage from "@/pages/IncotermPage";
import PriceRulesPage from "@/pages/PriceRulesPage";
import BundlesPage from "@/pages/BundlesPage";
import CombinationsPage from "@/pages/CombinationsPage";
import WebhooksPage from "@/pages/WebhooksPage";
import ShopifyScriptsPage from "@/pages/ShopifyScriptsPage";
import MetafieldsPage from "@/pages/MetafieldsPage";
import OrderSyncPage from "@/pages/OrderSyncPage";
import AdvancedRulesPage from "@/pages/AdvancedRulesPage";
import MarketplacesPage from "@/pages/MarketplacesPage";
import MarketplaceOrdersPage from "@/pages/MarketplaceOrdersPage";
import TrendyolSetupPage from "@/pages/TrendyolSetupPage";
import TranslationPage from "@/pages/TranslationPage";
import HelpPage from "@/pages/HelpPage";
import DistributionPage from "@/pages/DistributionPage";
import DistributionManagePage from "@/pages/DistributionManagePage";
// Nebim Hareketleri
import StokEklePage from "@/pages/nebim/StokEklePage";
import StokCikarPage from "@/pages/nebim/StokCikarPage";
import UrunAcPage from "@/pages/nebim/UrunAcPage";
import FotografEklePage from "@/pages/nebim/FotografEklePage";
import FiyatDegistirPage from "@/pages/nebim/FiyatDegistirPage";
import MagazaSevkPage from "@/pages/nebim/MagazaSevkPage";
import RaporlarPage from "@/pages/nebim/RaporlarPage";
// Raporlar
import GunlukSatisRaporuPage from "@/pages/nebim/raporlar/GunlukSatisRaporuPage";
import EnvanterRaporuPage from "@/pages/nebim/raporlar/EnvanterRaporuPage";
import UrunSatisAnaliziPage from "@/pages/nebim/raporlar/UrunSatisAnaliziPage";
import { useEffect } from "react";
import "@/App.css";

// Component to remove external watermarks
function WatermarkRemover() {
  useEffect(() => {
    const removeWatermark = () => {
      try {
        const allDivs = document.querySelectorAll('body > div:not(#root)');
        allDivs.forEach(el => {
          if (el.textContent && el.textContent.includes('Made with Emergent')) {
            el.style.cssText = 'display: none !important; visibility: hidden !important;';
          }
        });
      } catch (e) {
        // Ignore errors
      }
    };

    removeWatermark();
    const interval = setInterval(removeWatermark, 500);
    return () => clearInterval(interval);
  }, []);

  return null;
}

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Yükleniyor...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function ConnectionErrorBanner() {
  const { connectionError } = useAuth();
  if (!connectionError) return null;
  return (
    <div data-testid="connection-error-banner" className="fixed top-0 left-0 right-0 z-[9999] bg-red-600 text-white px-4 py-3 text-center text-sm font-medium shadow-lg">
      <span className="mr-2">&#9888;</span>{connectionError}
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Yükleniyor...</div>
      </div>
    );
  }
  
  return (
    <>
    <ConnectionErrorBanner />
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdvancedDashboardPage />} />
        <Route path="dashboard-old" element={<DashboardPage />} />
        <Route path="stores" element={<StoresPage />} />
        <Route path="settings/*" element={<SettingsPage />} />
        <Route path="marketplaces" element={<MarketplacesPage />} />
        <Route path="marketplace-orders" element={<MarketplaceOrdersPage />} />
        <Route path="trendyol-setup" element={<TrendyolSetupPage />} />
        <Route path="translation" element={<TranslationPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="shopify-products" element={<ShopifyProductsPage />} />
        <Route path="shopify-orders" element={<ShopifyOrdersPage />} />
        <Route path="auto-sync" element={<AutoSyncPage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="incoterm" element={<IncotermPage />} />
        <Route path="price-rules" element={<PriceRulesPage />} />
        <Route path="bundles" element={<BundlesPage />} />
        <Route path="combinations" element={<CombinationsPage />} />
        <Route path="webhooks" element={<WebhooksPage />} />
        <Route path="scripts" element={<ShopifyScriptsPage />} />
        <Route path="metafields" element={<MetafieldsPage />} />
        <Route path="order-sync" element={<OrderSyncPage />} />
        <Route path="advanced-rules" element={<AdvancedRulesPage />} />
        {/* Nebim Hareketleri */}
        <Route path="nebim/stok-ekle" element={<StokEklePage />} />
        <Route path="nebim/stok-cikar" element={<StokCikarPage />} />
        <Route path="nebim/urun-ac" element={<UrunAcPage />} />
        <Route path="nebim/fotograf-ekle" element={<FotografEklePage />} />
        <Route path="nebim/fiyat-degistir" element={<FiyatDegistirPage />} />
        <Route path="nebim/magaza-sevk" element={<MagazaSevkPage />} />
        <Route path="nebim/raporlar" element={<RaporlarPage />} />
        <Route path="nebim/raporlar/gunluk-satis" element={<GunlukSatisRaporuPage />} />
        <Route path="nebim/raporlar/envanter" element={<EnvanterRaporuPage />} />
        <Route path="nebim/raporlar/urun-satis-analizi" element={<UrunSatisAnaliziPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="distribution" element={<DistributionManagePage />} />
        <Route path="distribution-preview" element={<DistributionPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WatermarkRemover />
        <AppRoutes />
        <Toaster position="top-right" richColors toastOptions={{ style: { zIndex: 9998 } }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
